import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import Tooltip from "@material-ui/core/Tooltip";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Snackbar from "@material-ui/core/Snackbar";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CallIcon from "@material-ui/icons/CallOutlined";
import ChevronIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import MicIcon from "@material-ui/icons/MicNoneOutlined";
import ChatIcon from "@material-ui/icons/ChatBubbleOutline";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import PlayIcon from "@material-ui/icons/PlayArrowRounded";
import WarnIcon from "@material-ui/icons/ErrorOutlineRounded";
import clsx from "clsx";

import { toast } from "react-toastify";

import api from "../../services/api";
import { useMenuStyles } from "../../components/menuStyles";
import toastError from "../../errors/toastError";
import NumberSelect from "../../components/NumberSelect";
import AgentNumberBanner from "./AgentNumberBanner";
import AgentToolsPicker from "./AgentToolsPicker";
import AgentEventos from "./AgentEventos";
import AgentCorrecoes from "./AgentCorrecoes";
import AgentVersoes from "./AgentVersoes";
import AgentCanais from "./AgentCanais";
import AgentConversations from "./AgentConversations";
import AgentTransferEditor from "./AgentTransferEditor";
import AgentInputEditor from "./AgentInputEditor";
import AgentChatTester from "./AgentChatTester";
import AgentVoiceTester from "./AgentVoiceTester";
import CopilotPanel from "../Applications/CopilotPanel";
import { compile } from "../Applications/compile";
import { BASE_TOOLS, CREATE_TOOL, VOICES, buildAgentContext, buildAgentSystem } from "./agentCopilot";
import PersonaEditor from "./PersonaEditor";

// arquivos recebidos no WhatsApp (Opções avançadas) — espelho de
// backend/src/services/AgentFileService (FILE_KINDS / DEFAULT_FILE_SETTINGS)
const FILE_DEFAULTS = { maxMb: 15, types: ["pdf", "image", "doc", "xls"] };
const FILE_TYPE_OPTIONS = [
  { value: "pdf", label: "PDF" },
  { value: "image", label: "Imagem (JPG, PNG…)" },
  { value: "doc", label: "Word (DOC, DOCX)" },
  { value: "xls", label: "Excel (XLS, XLSX)" },
];

// Provedores de voz em tempo real (mesmos do bridge próprio). Cada um tem um
// conjunto de vozes; o ElevenLabs usa a voz configurada no agente da conta.
const PROVIDERS = [
  { value: "openai", label: "OpenAI Realtime — tools + estável", voices: ["alloy", "ash", "ballad", "coral", "echo", "sage", "shimmer", "verse"] },
  { value: "gptlive", label: "OpenAI GPT-Live — conversa mais natural (recomendado)", voices: ["bossa", "tempo", "marin"] },
  { value: "grok", label: "Grok Voice (xAI) — S2S, tools + busca em tempo real", voices: ["eve", "ara", "rex", "sal", "leo"] },
  { value: "eleven", label: "ElevenLabs ConvAI (premium, voz top, SEM toolbox)", voices: [] },
  { value: "gemini", label: "Gemini Live (Google) — pode cair (preview)", voices: ["Puck", "Charon", "Kore", "Fenrir", "Aoede", "Leda", "Orus", "Zephyr"] },
  { value: "nova", label: "Amazon Nova Sonic", voices: ["matthew", "tiffany", "amy"] },
];

// cérebros disponíveis no ElevenLabs ConvAI (override habilitado no agente).
// Obs.: function-calling no ElevenLabs depende muito do LLM — Qwen é fraco
// (não chama funções de forma confiável); Claude/GPT são fortes.
const ELEVEN_LLMS = [
  { value: "claude-sonnet-4-5", label: "Claude Sonnet 4.5 — equilibrado (bom em funções)" },
  { value: "claude-opus-4-7", label: "Claude Opus 4.7 (mais forte, mais lento)" },
  { value: "gpt-4o", label: "GPT-4o (bom em funções)" },
  { value: "grok-beta", label: "Grok (xAI) — grok-beta (mais antigo, função ok)" },
  { value: "qwen35-397b-a17b", label: "Qwen3 397B — ótima conversa, FRACO em funções" },
  { value: "gemini-2.5-flash", label: "Gemini 2.5 Flash (rápido)" },
];

const useStyles = makeStyles(theme => ({
  page: { flex: 1, minWidth: 0, height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: {
    width: "100%", maxWidth: 1000, margin: "0 auto", padding: theme.spacing(4, 4, 8), "& .MuiOutlinedInput-root": { background: "#fff" },
    // mesma escala de campos do painel de configuração do nó (NodeConfigShell):
    // o MUI põe 16px em todo input/label, maior que o texto ao redor. Vale p/
    // todas as abas (Geral, Voz, Canais, Eventos, Correções…).
    "& .MuiInputBase-input": { fontSize: 13, letterSpacing: 0 },
    "& .MuiInputLabel-root": { fontSize: 13, letterSpacing: 0 },
    "& .MuiFormHelperText-root": { fontSize: 11.5 },
    "& .MuiFormControlLabel-label": { fontSize: 13 },
    "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
  },

  // cabeçalho: título + subtítulo (esq.) e ações Testar / Salvar (dir.)
  header: { display: "flex", alignItems: "flex-start", gap: 14, paddingBottom: 22 },
  headText: { flex: 1, minWidth: 0 },
  back: { display: "inline-flex", alignItems: "center", background: "none", border: "none", padding: 0, marginBottom: 8, fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: theme.app.textTertiary, cursor: "pointer", "&:hover": { color: theme.app.ink } },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 24, letterSpacing: "-0.01em", color: theme.app.ink, margin: 0 },
  subtitle: { color: theme.app.textSecondary, fontSize: 14, marginTop: 5, maxWidth: 520, lineHeight: 1.5 },
  testBtn: { flexShrink: 0, textTransform: "none", fontSize: 13, fontWeight: 600, color: theme.app.ink, background: "#fff", border: `1px solid ${theme.app.borderInput}`, borderRadius: 8, padding: "8px 20px", boxShadow: "none", "&:hover": { background: theme.app.hover, boxShadow: "none" } },
  saveBtn: { flexShrink: 0, textTransform: "none", fontSize: 13, fontWeight: 600, color: "#fff", background: theme.app.dark, borderRadius: 8, padding: "8px 20px", boxShadow: "none", "&:hover": { background: theme.app.ink, boxShadow: "none" }, "&.Mui-disabled": { background: theme.app.border, color: theme.app.textTertiary } },

  // abas do editor (ativo em verde, estilo das demais telas)
  tabs: { display: "flex", alignItems: "center", gap: 26, marginBottom: theme.spacing(1), borderBottom: `1px solid ${theme.app.border}` },
  tab: { border: "none", background: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 600, color: theme.app.textTertiary, padding: "4px 2px 12px", marginBottom: -1, borderBottom: "2.5px solid transparent", "&:hover": { color: theme.app.ink } },
  tabOn: { color: theme.app.green, borderBottomColor: theme.app.green },
  // sub-abas da aba Canais (Telefone / WhatsApp) — segmentado, como Editar/Visualizar da persona
  subTabs: { display: "inline-flex", background: theme.app.chip || "#EEF0F3", borderRadius: 10, padding: 3, margin: "18px 0 6px" },
  subTab: {
    display: "inline-flex", alignItems: "center", gap: 7, border: "none", background: "transparent", cursor: "pointer",
    fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: theme.app.textTertiary, padding: "7px 14px", borderRadius: 8,
    "& svg": { fontSize: 17 }, "&:hover": { color: theme.app.ink },
  },
  subTabOn: { background: "#fff", color: theme.app.ink, boxShadow: "0 1px 2px rgba(16,20,26,.12)" },

  // placeholder "recurso indisponível" (aba Conversas)
  soon: { textAlign: "center", padding: theme.spacing(9, 3), maxWidth: 460, margin: "0 auto" },
  soonIcon: { width: 60, height: 60, borderRadius: 999, background: theme.app.lineSoft, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16, "& svg": { fontSize: 30, color: theme.app.textTertiary } },
  soonTitle: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 17, color: theme.app.ink, marginBottom: 6 },
  soonText: { fontSize: 13.5, color: theme.app.textSecondary, lineHeight: 1.6 },
  soonBadge: { display: "inline-block", marginTop: 16, fontSize: 11.5, fontWeight: 600, color: theme.app.textTertiary, background: theme.app.lineSoft, borderRadius: 999, padding: "5px 14px" },

  // seção em duas colunas (rótulo + descrição | campos)
  section: { display: "grid", gridTemplateColumns: "260px 1fr", gap: 44, alignItems: "start", borderTop: `1px solid ${theme.app.border}`, padding: "26px 0" },
  // "Opções avançadas" (recolhida): link discreto que abre/fecha a seção
  advToggle: {
    display: "inline-flex", alignItems: "center", gap: 4, margin: "18px 0 4px", padding: 0,
    border: "none", background: "none", cursor: "pointer", fontFamily: "inherit",
    fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
    color: theme.app.textSecondary, "& svg": { fontSize: 18 }, "&:hover": { color: theme.app.ink },
  },
  secLabel: { fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textSecondary },
  secDesc: { fontSize: 13.5, color: theme.app.textTertiary, marginTop: 8, lineHeight: 1.5 },
  secRight: { minWidth: 0 },
  help: { fontSize: 12.5, color: theme.app.textTertiary, marginTop: 7, marginBottom: 4, lineHeight: 1.4 },
  warnBox: { display: "flex", gap: 10, alignItems: "flex-start", background: "#FBF4E6", border: "1px solid #EBD9B0", borderRadius: 12, padding: "12px 14px", fontSize: 13, color: "#8A6D1F", lineHeight: 1.5, marginBottom: 14, "& svg": { fontSize: 19, flexShrink: 0, marginTop: 1, color: "#C77A12" }, "& b": { color: "#6f560f" } },
  row: { display: "flex", gap: 12, alignItems: "flex-start" },
  // tocando: botão verde com barrinhas de equalizador animadas (clicar para)
  playBtnOn: { borderColor: theme.app.green, color: theme.app.green, background: "#EFF8D6", "&:hover": { background: "#E4F2C4" } },
  eq: {
    display: "inline-flex", alignItems: "flex-end", gap: 2, height: 16,
    "& i": { display: "block", width: 3, height: "100%", borderRadius: 2, background: "currentColor", transformOrigin: "bottom", animation: "$eqBar .9s ease-in-out infinite" },
    "& i:nth-child(2)": { animationDelay: "-.6s" },
    "& i:nth-child(3)": { animationDelay: "-.3s" },
    "& i:nth-child(4)": { animationDelay: "-.75s" },
  },
  "@keyframes eqBar": { "0%,100%": { transform: "scaleY(.3)" }, "50%": { transform: "scaleY(1)" } },
  playBtn: { flexShrink: 0, color: theme.app.ink, border: `1px solid ${theme.app.borderInput}`, borderRadius: 999, width: 40, height: 40, "&:hover": { background: theme.app.hover, borderColor: theme.app.green, color: theme.app.green }, "&.Mui-disabled": { color: theme.app.textTertiary } },


  // conteúdo interno das seções
  field: { marginBottom: 12 },
  // aba Ferramentas: largura cheia (sem a coluna de rótulo) p/ nomes/descrições inteiros
  toolsTab: { borderTop: `1px solid ${theme.app.border}`, padding: "26px 0", maxWidth: 860 },
}));

const AgentEditor = () => {
  const classes = useStyles();
  const estilos = useMenuStyles();
  const { agentId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [agent, setAgent] = useState(null);
  const [name, setName] = useState("");
  const [cfg, setCfg] = useState({ persona: "", greeting: "", provider: "gemini", voice: "", elevenLlm: "claude-sonnet-4-5", chatModel: "gpt-4o", transferDestinations: [], toolIds: [], systemTools: [], outputs: [], inputs: [], humanize: {}, transferToHuman: false, inboundHistory: "considerar" });
  const [tools, setTools] = useState([]);
  const [connectors, setConnectors] = useState([]); // p/ gating de ferramentas built-in (ex.: Atende)
  const [datasets, setDatasets] = useState([]); // tabelas da conta (ferramenta de sistema "Consultar tabela")
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [callTo, setCallTo] = useState("");
  const [callFrom, setCallFrom] = useState("");
  const [calling, setCalling] = useState(false);
  const [testMenu, setTestMenu] = useState(null); // anchor do menu "Testar"
  const [elevenVoices, setElevenVoices] = useState([]); // catálogo de vozes do ElevenLabs
  const [tab, setTab] = useState("geral"); // geral | canais | ferramentas | eventos | correcoes | versoes | conversas
  const [advOpen, setAdvOpen] = useState(false); // "Opções avançadas" da aba Geral: recolhida até o usuário abrir
  const [canal, setCanal] = useState("voz"); // sub-aba de Canais: voz | whatsapp
  // deep-link ?tab=conversas&conv=<id|número> (ex.: botão "Ver conversa" da SAÍDA
  // do nó "Iniciar conversa com agente de IA"). Lido 1x na montagem.
  const [deepLinkConv] = useState(() => new URLSearchParams(location.search).get("conv") || null);
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    // abas antigas: Publicação foi fundida em Canais; Voz virou Canais → Telefone
    if (t === "publicacao" || t === "voz") setTab("canais");
    else if (t === "whatsapp") { setTab("canais"); setCanal("whatsapp"); }
    else if (t) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [linkedNumbers, setLinkedNumbers] = useState([]); // números que atendem este agente
  const [copilotOpen, setCopilotOpen] = useState(false);
  // seed vindo da tela-porta (AgentHero): descrição p/ o copiloto montar sozinho.
  // Lido 1x na montagem (o state some num refresh — aí nasce vazio, sem re-semear).
  const [copilotSeed] = useState(() => (location.state && location.state.seed) || "");
  // vindo de "Conversas recentes" ou da tela-porta: abre o copilot já na tela
  useEffect(() => {
    if (location.state && (location.state.openCopilot || location.state.seed)) setCopilotOpen(true);
  }, [location.state]);

  // enquanto o copiloto escreve, a persona fica em leitura; e o set_persona é
  // "digitado" na tela (efeito de escrita).
  const [copilotBusy, setCopilotBusy] = useState(false);
  const [personaTyping, setPersonaTyping] = useState(false);
  const typeTimerRef = useRef(null);
  const personaTargetRef = useRef(null); // texto final durante a digitação (o copiloto lê este)
  useEffect(() => () => { if (typeTimerRef.current) clearInterval(typeTimerRef.current); }, []);

  // estado vivo para o copilot — ref estável evita stale closure no loop async
  const stateRef = useRef({});
  stateRef.current = {
    name,
    // durante a digitação, o copiloto lê o texto FINAL (não o parcial na tela)
    persona: personaTargetRef.current != null ? personaTargetRef.current : cfg.persona,
    greeting: cfg.greeting, provider: cfg.provider, voice: cfg.voice,
    transferDestinations: cfg.transferDestinations, outputs: cfg.outputs, inputs: cfg.inputs, toolIds: cfg.toolIds, systemTools: cfg.systemTools,
  };
  const copilotGetState = useCallback(() => ({ ...stateRef.current }), []);
  const copilotApply = useCallback(patch => {
    if (patch.name != null) setName(String(patch.name));
    const rest = { ...patch }; delete rest.name;
    if (Object.keys(rest).length) setCfg(c => ({ ...c, ...rest }));
  }, []);

  // "digita" a persona na tela (efeito de escrita) quando o copiloto a define.
  // O valor final entra em personaTargetRef na hora (fonte da verdade p/ salvar/ler).
  const typePersona = useCallback(full => {
    const text = String(full || "");
    if (typeTimerRef.current) { clearInterval(typeTimerRef.current); typeTimerRef.current = null; }
    if (!text) { personaTargetRef.current = null; setPersonaTyping(false); setCfg(c => ({ ...c, persona: "" })); return; }
    personaTargetRef.current = text;
    const TICK = 16;
    const step = Math.max(3, Math.ceil(text.length / (1300 / TICK))); // ~1.3s no total
    let i = 0;
    setPersonaTyping(true);
    setCfg(c => ({ ...c, persona: "" }));
    typeTimerRef.current = setInterval(() => {
      i = Math.min(text.length, i + step);
      const slice = text.slice(0, i);
      setCfg(c => ({ ...c, persona: slice }));
      if (i >= text.length) {
        clearInterval(typeTimerRef.current); typeTimerRef.current = null;
        personaTargetRef.current = null;
        setPersonaTyping(false);
      }
    }, TICK);
  }, []);
  const reloadTools = useCallback(async () => {
    const pid = agent && agent.projectId;
    if (!pid) return;
    try {
      const { data } = await api.get("/applications", { params: { projectId: pid } });
      setTools((data.applications || []).filter(a => a.triggerType === "tool"));
    } catch (e) { /* silencioso */ }
  }, [agent]);

  // lista de ferramentas viva (ref) para o perfil/executor lerem sem stale closure
  const toolsRef = useRef(tools);
  toolsRef.current = tools;
  const toSlug = s => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9_]/g, "_").replace(/^_+|_+$/g, "") || "campo";

  // cria a tool-app HTTP: gera o flow visual e compila com o MESMO compile do Builder
  const buildToolApp = useCallback(async spec => {
    const inputParams = (spec.inputParams || []).map(p => ({
      name: toSlug(p.name), type: p.type === "number" ? "number" : p.type === "boolean" ? "boolean" : "string",
      description: p.description || "", required: p.required !== false,
    }));
    const sample = {};
    inputParams.forEach(p => { sample[p.name] = p.type === "number" ? 0 : p.type === "boolean" ? true : "exemplo"; });
    const start = { id: "start", type: "triggerNode", position: { x: 400, y: 40 }, data: { verbKey: "__trigger__", triggerType: "tool", inputParams, sample: { input: sample } } };
    const http = { id: "n_1", type: "verbNode", position: { x: 400, y: 220 }, data: { verbKey: "http_request", method: String(spec.method || "GET").toUpperCase(), url: spec.url || "", headers: spec.headers ? JSON.stringify(spec.headers) : "", body: spec.body || "", saveAs: "" } };
    const resultRef = spec.resultPath ? `{{steps.http_request.json.${spec.resultPath}}}` : "{{steps.http_request.json}}";
    const ret = { id: "n_2", type: "verbNode", position: { x: 400, y: 400 }, data: { verbKey: "return", value: resultRef, description: spec.description || "" } };
    const e1 = { id: "e_start_next_n_1", source: "start", sourceHandle: "next", target: "n_1", type: "smoothstep", arrowHeadType: "arrowclosed" };
    const e2 = { id: "e_n_1_next_n_2", source: "n_1", sourceHandle: "next", target: "n_2", type: "smoothstep", arrowHeadType: "arrowclosed" };
    const elements = [start, http, e1, ret, e2];
    const { definition } = compile(elements, { name: spec.name, description: spec.description || "", trigger: "outbound" });
    const { data } = await api.post("/applications", {
      name: spec.name, description: spec.description || "", triggerType: "tool", trigger: "outbound",
      projectId: agent && agent.projectId, triggerConfig: { inputParams }, flow: elements, definition,
    });
    return data && data.id;
  }, [agent]);

  // executor das tools do copilot de agente (muta a config e cria ferramentas)
  const runAgentCopilotTool = useCallback(async (name, input2) => {
    const a = input2 || {};
    try {
      if (name === "read_agent") return { agent: copilotGetState(), tools: (toolsRef.current || []).map(t => ({ id: t.id, name: t.name, description: t.description })), voices: VOICES };
      if (name === "fetch_url") {
        const url = String(a.url || "").trim();
        if (!url) return { error: "Informe a URL." };
        const { data } = await api.post("/ai/fetch-url", { url });
        return { title: data.title || "", pages: data.pages || [], words: data.words || 0, truncated: !!data.truncated, text: data.text || "" };
      }
      if (name === "set_name") { copilotApply({ name: String(a.name || "") }); return { ok: true }; }
      if (name === "set_persona") { typePersona(String(a.persona || "")); return { ok: true }; }
      if (name === "set_greeting") { copilotApply({ greeting: String(a.greeting || "") }); return { ok: true }; }
      if (name === "set_voice") {
        const provider = String(a.provider || "gptlive"); const list = VOICES[provider];
        if (list && list.length && a.voice && !list.includes(a.voice)) return { error: `Voz '${a.voice}' não existe em ${provider}. Vozes: ${list.join(", ")}.`, retryable: true };
        copilotApply({ provider, voice: String(a.voice || "") }); return { ok: true };
      }
      if (name === "set_transfers") { copilotApply({ transferDestinations: Array.isArray(a.transfers) ? a.transfers : [] }); return { ok: true }; }
      if (name === "set_outputs") { copilotApply({ outputs: Array.isArray(a.outputs) ? a.outputs : [] }); return { ok: true }; }
      if (name === "attach_tool") { const id = Number(a.tool_id); if (!id) return { error: "tool_id inválido." }; copilotApply({ toolIds: Array.from(new Set([...(copilotGetState().toolIds || []), id])) }); return { ok: true }; }
      if (name === "detach_tool") { const id = Number(a.tool_id); copilotApply({ toolIds: (copilotGetState().toolIds || []).filter(t => Number(t) !== id) }); return { ok: true }; }
      if (name === "request_tool_builder") return { enabled: true, note: "create_tool disponível — crie a ferramenta HTTP agora.", __extra: { toolBuilder: true } };
      if (name === "create_tool") {
        if (!a.name || !a.url || !a.method) return { error: "name, method e url são obrigatórios." };
        const id = await buildToolApp(a);
        if (!id) return { error: "Não consegui criar a ferramenta." };
        copilotApply({ toolIds: Array.from(new Set([...(copilotGetState().toolIds || []), id])) });
        if (reloadTools) reloadTools();
        return { ok: true, tool_id: id, name: a.name };
      }
      return { error: `Ferramenta desconhecida: ${name}` };
    } catch (err) {
      return { error: (err && err.response && err.response.data && err.response.data.error) || (err && err.message) || "Falha na ação." };
    }
  }, [copilotGetState, copilotApply, reloadTools, buildToolApp, typePersona]);

  // perfil do copilot de agente para o CopilotPanel (mesmo componente do fluxo)
  const agentProfile = useMemo(() => ({
    title: "Copilot do agente",
    ctxChip: "este agente",
    emptyTitle: "Descreva o agente que você quer",
    emptyDesc: "Eu configuro persona, voz, saudação e ferramentas.",
    examples: [
      "Atendente de motel que consulta disponibilidade de quartos na nossa API e transfere pra recepção.",
      "Agente de cobrança educado que confirma o pagamento e registra o resultado.",
    ],
    quickReplies: [],
    placeholder: "Descreva o agente…",
    initialExtra: { toolBuilder: false },
    loadContext: async () => ({ ready: true }),
    buildSystem: (ctx, extra) => buildAgentSystem(buildAgentContext(copilotGetState(), toolsRef.current || [], null), { toolBuilder: !!(extra && extra.toolBuilder) }),
    buildTools: (ctx, extra) => (extra && extra.toolBuilder ? [...BASE_TOOLS, CREATE_TOOL] : BASE_TOOLS),
  }), [copilotGetState]);

  const loadLinked = useCallback(async () => {
    try {
      const { data } = await api.get(`/agents/${agentId}/numbers`);
      setLinkedNumbers(data.numbers || []);
    } catch (e) {
      /* silencioso */
    }
  }, [agentId]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/applications/${agentId}`);
      setAgent(data);
      setName(data.name || "");
      const c = data.triggerConfig || {};
      setCfg({
        // tudo o que está gravado (gravação, caixa postal, arquivos…) — senão o
        // próximo salvar apagava as chaves que esta lista não conhece
        ...c,
        persona: c.persona || "",
        greeting: c.greeting || "",
        provider: c.provider || "gemini",
        voice: c.voice || "",
        elevenLlm: c.elevenLlm || "claude-sonnet-4-5",
        chatModel: c.chatModel || "gpt-4o",
        // migra o campo legado (destino único) para a lista de destinos
        transferDestinations: Array.isArray(c.transferDestinations)
          ? c.transferDestinations
          : c.humanTransferTo
          ? [{ label: "Atendente", number: c.humanTransferTo }]
          : [],
        toolIds: Array.isArray(c.toolIds) ? c.toolIds : [],
        systemTools: Array.isArray(c.systemTools) ? c.systemTools : [],
        outputs: Array.isArray(c.outputs) ? c.outputs : [],
        inputs: Array.isArray(c.inputs) ? c.inputs : [],
        humanize: c.humanize && typeof c.humanize === "object" ? c.humanize : {},
        transferToHuman: c.transferToHuman === true,
        inboundHistory: c.inboundHistory || "considerar",
      });
      // ferramentas disponíveis no mesmo projeto
      if (data.projectId) {
        const { data: apps } = await api.get("/applications", { params: { projectId: data.projectId } });
        setTools((apps.applications || []).filter(a => a.triggerType === "tool"));
      }
      // conectores do dono (p/ gating de ferramentas built-in, ex.: Atende)
      try {
        const { data: conns } = await api.get("/connectors");
        setConnectors(conns.connectors || []);
      } catch (e) {
        /* ignore */
      }
      try {
        const { data: ds } = await api.get("/datasets");
        setDatasets(ds.datasets || []);
      } catch (e) {
        /* ignore */
      }
    } catch (err) {
      toastError(err);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadLinked();
  }, [loadLinked]);

  // carrega o catálogo de vozes do ElevenLabs quando o provedor é eleven
  useEffect(() => {
    if (cfg.provider !== "eleven" || elevenVoices.length) return;
    (async () => {
      try {
        const { data } = await api.get("/agents/eleven-voices");
        setElevenVoices(data.voices || []);
      } catch (e) {
        /* silencioso — cai no campo vazio */
      }
    })();
  }, [cfg.provider, elevenVoices.length]);

  const set = (k, v) => setCfg(c => ({ ...c, [k]: v }));
  // config de humanização (subobjeto triggerConfig.humanize). Vazio = defaults do backend.
  const h = cfg.humanize || {};
  const setH = (k, v) => setCfg(c => ({ ...c, humanize: { ...(c.humanize || {}), [k]: v } }));
  const toggleTool = id =>
    setCfg(c => ({
      ...c,
      toolIds: c.toolIds.includes(id) ? c.toolIds.filter(x => x !== id) : [...c.toolIds, id],
    }));

  const provider = PROVIDERS.find(p => p.value === cfg.provider) || PROVIDERS[0];

  // #4: prévia de voz — toca um áudio pré-gravado por voz/provedor.
  // Arquivos em /public/voice-previews/<provedor>-<voz>.mp3 (gerar com o script
  // scripts/gen-voice-previews). ElevenLabs usa Voice ID livre, sem prévia.
  // prévia da voz: idle | loading (gerando no backend) | playing (anima o botão)
  const [preview, setPreview] = useState("idle");
  const previewAudio = useRef(null);
  const stopPreview = useCallback(() => {
    if (previewAudio.current) {
      previewAudio.current.pause();
      previewAudio.current = null;
    }
    setPreview("idle");
  }, []);
  // trocou de voz/provedor (ou saiu da tela): para o que estiver tocando
  useEffect(() => stopPreview, [cfg.voice, cfg.provider, stopPreview]);

  // toca a 1ª fonte que funcionar; resolve true se tocou
  const playFrom = async srcs => {
    for (const src of srcs) {
      const a = new Audio(src);
      try {
        await a.play();
        previewAudio.current = a;
        setPreview("playing");
        a.onended = () => { if (previewAudio.current === a) stopPreview(); };
        return true;
      } catch (e) {
        /* próxima fonte */
      }
    }
    return false;
  };

  const playPreview = async () => {
    if (preview !== "idle") return stopPreview(); // clicar de novo para
    if (!cfg.voice) {
      toast.info(cfg.provider === "eleven" ? "Informe o Voice ID do ElevenLabs." : "Selecione uma voz primeiro.");
      return;
    }
    // ElevenLabs: toca o arquivo pré-gravado por Voice ID; se não existir (outro
    // ID), gera sob demanda no backend.
    if (cfg.provider === "eleven") {
      if (await playFrom([`/voice-previews/eleven-${cfg.voice}.mp3`])) return;
      setPreview("loading");
      try {
        const { data } = await api.get("/agents/voice-preview", { params: { voice: cfg.voice }, responseType: "blob" });
        if (!(await playFrom([URL.createObjectURL(data)]))) throw new Error("play");
      } catch (e) {
        setPreview("idle");
        toast.info("Não consegui gerar a prévia dessa voz do ElevenLabs.");
      }
      return;
    }
    // demais provedores: arquivos pré-gravados (mp3/wav).
    const base = `/voice-previews/${cfg.provider}-${cfg.voice}`;
    if (!(await playFrom([`${base}.mp3`, `${base}.wav`]))) toast.info("Prévia desta voz ainda não foi gerada.");
  };

  const save = async () => {
    if (!agent) return;
    setSaving(true);
    try {
      await api.put(`/applications/${agentId}`, {
        name: name.trim() || agent.name,
        triggerConfig: { ...cfg, toolIds: cfg.toolIds },
      });
      setAgent(a => ({ ...a, name: name.trim() || a.name }));
      setSaved(true);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const startCall = async () => {
    if (!callTo.trim() || !callFrom.trim()) return;
    setCalling(true);
    try {
      // garante que a persona/voz/toolbox atual está salva antes de ligar
      await api.put(`/applications/${agentId}`, { name: name.trim() || agent.name, triggerConfig: cfg });
      await api.post(`/applications/${agentId}/test-call`, { to: callTo.trim(), from: callFrom.trim() });
      toast.success("Ligação iniciada! O agente vai chamar o número.");
      setCallOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setCalling(false);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        {/* nudge de número só para agentes JÁ SALVOS (config persistida) — não o
            shell recém-criado. "salvo" = salvou nesta sessão OU updatedAt avançou. */}
        {agent && linkedNumbers.length === 0 &&
          (saved || (agent.updatedAt && agent.createdAt && new Date(agent.updatedAt) - new Date(agent.createdAt) > 2000)) && (
          <AgentNumberBanner agentId={agentId} agentName={agent.name} projectId={agent.projectId} onDone={loadLinked} />
        )}
        <header className={classes.header}>
          <div className={classes.headText}>
            <button type="button" className={classes.back} onClick={() => history.push("/agents")}>← Agentes de IA</button>
            <h1 className={classes.title}>{agent ? agent.name : "Agente"}</h1>
            <div className={classes.subtitle}>
              Configure a persona, a voz e as ferramentas que este agente usa nas ligações.
            </div>
          </div>
          <Button className={classes.testBtn} endIcon={<ChevronIcon />} onClick={e => setTestMenu(e.currentTarget)}>
            Testar
          </Button>
          <Button className={classes.saveBtn} onClick={save} disabled={saving}>
            {saving ? "Salvando…" : "Salvar agente"}
          </Button>
        </header>

        <CopilotPanel
          open={copilotOpen}
          onOpen={() => setCopilotOpen(true)}
          onClose={() => setCopilotOpen(false)}
          runTool={runAgentCopilotTool}
          profile={agentProfile}
          projectId={agent && agent.projectId}
          appId={agentId}
          seed={copilotSeed}
          demo={!!copilotSeed}
          onBusyChange={setCopilotBusy}
        />

        <div className={classes.tabs}>
          <button type="button" className={clsx(classes.tab, tab === "geral" && classes.tabOn)} onClick={() => setTab("geral")}>Geral</button>
          <button type="button" className={clsx(classes.tab, tab === "canais" && classes.tabOn)} onClick={() => setTab("canais")}>Canais</button>
          <button type="button" className={clsx(classes.tab, tab === "ferramentas" && classes.tabOn)} onClick={() => setTab("ferramentas")}>Ferramentas</button>
          <button type="button" className={clsx(classes.tab, tab === "eventos" && classes.tabOn)} onClick={() => setTab("eventos")}>Subscrições</button>
          <button type="button" className={clsx(classes.tab, tab === "correcoes" && classes.tabOn)} onClick={() => setTab("correcoes")}>Correções</button>
          <button type="button" className={clsx(classes.tab, tab === "versoes" && classes.tabOn)} onClick={() => setTab("versoes")}>Versões</button>
          <button type="button" className={clsx(classes.tab, tab === "conversas" && classes.tabOn)} onClick={() => setTab("conversas")}>Conversas</button>
        </div>

        {tab === "geral" && (
        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Identificação</div>
            <div className={classes.secDesc}>Como este agente aparece na sua lista.</div>
          </div>
          <div className={classes.secRight}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Nome do agente"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
        </div>
        )}

        {tab === "geral" && (
        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Persona</div>
            <div className={classes.secDesc}>A instrução que a IA segue e a primeira coisa que ela fala.</div>
          </div>
          <div className={classes.secRight}>
            {/* agente já salvo abre em Visualizar; novo (recém-criado) em Editar.
                key remonta quando o agente carrega, aplicando o modo correto. */}
            <PersonaEditor
              key={agent ? "loaded" : "loading"}
              defaultMode={agent && agent.updatedAt && agent.createdAt && new Date(agent.updatedAt) - new Date(agent.createdAt) > 2000 ? "view" : "edit"}
              value={cfg.persona}
              onChange={v => set("persona", v)}
              readOnly={copilotBusy || personaTyping}
            />
            <TextField
              style={{ marginTop: 14 }}
              fullWidth
              variant="outlined"
              size="small"
              label="Saudação inicial"
              placeholder="Ex.: Oi! Aqui é a assistente da Loja X, em que posso ajudar?"
              value={cfg.greeting}
              onChange={e => set("greeting", e.target.value)}
            />
          </div>
        </div>
        )}

        {tab === "ferramentas" && (
        <div className={classes.toolsTab}>
          {cfg.provider === "eleven" && (
            <div className={classes.warnBox}>
              <WarnIcon />
              <span>O <b>ElevenLabs não suporta ferramentas</b> (function calling). Para associar ferramentas, escolha outro provedor de voz em <b>Canais → Telefone (voz)</b>.</span>
            </div>
          )}
          <AgentToolsPicker
            tools={tools}
            toolIds={cfg.toolIds}
            onToggleTool={toggleTool}
            transferToHuman={cfg.transferToHuman === true}
            onTransferToHuman={v => set("transferToHuman", v)}
            atendeAvailable={connectors.some(c => c.type === "atende")}
            disabled={cfg.provider === "eleven"}
            systemTools={cfg.systemTools}
            onSystemTools={v => set("systemTools", v)}
            datasets={datasets}
            connectors={connectors}
            inputs={cfg.inputs}
          />
        </div>
        )}

        {tab === "geral" && (
        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Entradas</div>
            <div className={classes.secDesc}>Dados que o fluxo informa ao iniciar a conversa — ligação ou WhatsApp (ex.: nome do cliente).</div>
          </div>
          <div className={classes.secRight}>
            <AgentInputEditor value={cfg.inputs} onChange={v => set("inputs", v)} />
          </div>
        </div>
        )}

        {tab === "geral" && (
          <button type="button" className={classes.advToggle} onClick={() => setAdvOpen(o => !o)} aria-expanded={advOpen}>
            {advOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />} Opções avançadas
          </button>
        )}

        {tab === "geral" && advOpen && (
        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Arquivos recebidos</div>
            <div className={classes.secDesc}>Arquivos que o contato manda no WhatsApp: ficam no Armazenamento, em Uploads › Agentes › {name || (agent && agent.name) || "este agente"}.</div>
          </div>
          <div className={classes.secRight}>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              label="Tamanho máximo por arquivo (MB)"
              value={(cfg.files && cfg.files.maxMb) != null ? cfg.files.maxMb : 15}
              onChange={e => set("files", { ...FILE_DEFAULTS, ...(cfg.files || {}), maxMb: e.target.value === "" ? "" : Math.max(1, Math.min(100, Number(e.target.value) || 1)) })}
              inputProps={{ min: 1, max: 100 }}
              style={{ maxWidth: 260 }}
              helperText="Acima disso, a IA avisa o contato que o arquivo é muito grande."
            />
            <div className={classes.help} style={{ marginTop: 12 }}>Formatos aceitos</div>
            <div className={classes.row}>
              {FILE_TYPE_OPTIONS.map(o => {
                const types = (cfg.files && Array.isArray(cfg.files.types)) ? cfg.files.types : FILE_DEFAULTS.types;
                return (
                  <FormControlLabel
                    key={o.value}
                    control={
                      <Checkbox
                        color="primary"
                        checked={types.includes(o.value)}
                        onChange={e => set("files", { ...FILE_DEFAULTS, ...(cfg.files || {}), types: e.target.checked ? [...types, o.value] : types.filter(t => t !== o.value) })}
                      />
                    }
                    label={o.label}
                  />
                );
              })}
            </div>
          </div>
        </div>
        )}

        {tab === "canais" && agent && (
          <div className={classes.subTabs}>
            <button type="button" className={clsx(classes.subTab, canal === "voz" && classes.subTabOn)} onClick={() => setCanal("voz")}>
              <CallIcon /> Telefone (voz)
            </button>
            <button type="button" className={clsx(classes.subTab, canal === "whatsapp" && classes.subTabOn)} onClick={() => setCanal("whatsapp")}>
              <WhatsAppIcon /> WhatsApp
            </button>
          </div>
        )}

        {tab === "canais" && agent && canal === "voz" && (
        <>
          <AgentCanais only="voz" agentId={agentId} agentName={agent.name} projectId={agent.projectId} numbers={linkedNumbers} onChange={loadLinked} />
        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Voz</div>
            <div className={classes.secDesc}>Quem fala com o cliente. Ouça antes de escolher.</div>
          </div>
          <div className={classes.secRight}>
            <div className={classes.row}>
              <TextField
                style={{ flex: 1 }}
                select
                variant="outlined"
                size="small"
                label="Provedor"
                value={cfg.provider}
                onChange={e => set("provider", e.target.value)}
              >
                {PROVIDERS.map(p => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </TextField>
              {cfg.provider === "eleven" ? (
                <TextField
                  style={{ flex: 1 }}
                  select
                  variant="outlined"
                  size="small"
                  label="Voz"
                  value={cfg.voice || ""}
                  onChange={e => set("voice", e.target.value)}
                  helperText={elevenVoices.length ? undefined : "Carregando vozes da conta…"}
                >
                  <MenuItem value="">Selecionar…</MenuItem>
                  {elevenVoices.map(v => (
                    <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  style={{ flex: 1 }}
                  select
                  variant="outlined"
                  size="small"
                  label="Voz"
                  value={cfg.voice || ""}
                  onChange={e => set("voice", e.target.value)}
                >
                  <MenuItem value="">Selecionar…</MenuItem>
                  {provider.voices.map(v => (
                    <MenuItem key={v} value={v}>{v}</MenuItem>
                  ))}
                </TextField>
              )}
              <Tooltip title={preview !== "idle" ? "Parar" : cfg.provider === "eleven" ? "Ouvir a voz (gera na hora)" : "Ouvir a voz"}>
                <span>
                  <IconButton
                    className={clsx(classes.playBtn, preview !== "idle" && classes.playBtnOn)}
                    onClick={playPreview}
                    disabled={!cfg.voice}
                  >
                    {preview === "playing" ? (
                      <span className={classes.eq}><i /><i /><i /><i /></span>
                    ) : preview === "loading" ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PlayIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </div>
            {cfg.provider === "eleven" && (
              <TextField
                style={{ marginTop: 12 }}
                select
                fullWidth
                variant="outlined"
                size="small"
                label="Cérebro (LLM)"
                helperText="Qual modelo raciocina no ElevenLabs. Qwen tem ótimo raciocínio com baixa latência; Opus é mais forte mas mais lento."
                value={cfg.elevenLlm || "claude-sonnet-4-5"}
                onChange={e => set("elevenLlm", e.target.value)}
              >
                {ELEVEN_LLMS.map(l => (
                  <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                ))}
              </TextField>
            )}
            {cfg.provider === "eleven" && cfg.toolIds.length > 0 && (
              <div className={classes.warnBox} style={{ marginTop: 14, marginBottom: 0 }}>
                <WarnIcon />
                <span>O <b>ElevenLabs não usa ferramentas</b> (function calling). Sua(s) {cfg.toolIds.length} ferramenta(s) ativa(s) serão <b>ignoradas</b> nas ligações — troque o provedor de voz para poder usá-las.</span>
              </div>
            )}
          </div>
        </div>
        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Caixa postal</div>
            <div className={classes.secDesc}>O que fazer quando uma ligação feita pelo agente cai em caixa postal ou numa mensagem gravada da operadora.</div>
          </div>
          <div className={classes.secRight}>
            <TextField
              select
              fullWidth
              variant="outlined"
              size="small"
              label="Caixa postal"
              value={cfg.voicemail || "hangup"}
              onChange={e => set("voicemail", e.target.value)}
            >
              <MenuItem value="hangup">Detectar e desligar</MenuItem>
              <MenuItem value="message">Detectar e deixar recado</MenuItem>
              <MenuItem value="off">Não detectar</MenuItem>
            </TextField>
            {cfg.voicemail === "message" && (
              <TextField
                style={{ marginTop: 12 }}
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                size="small"
                label="Recado"
                placeholder="Ex.: Oi, aqui é a Ana da BR DID. Vou tentar falar com você mais tarde. Obrigada!"
                value={cfg.voicemailMessage || ""}
                onChange={e => set("voicemailMessage", e.target.value)}
                error={!String(cfg.voicemailMessage || "").trim()}
                helperText={String(cfg.voicemailMessage || "").trim() ? "Dito depois do sinal; em seguida a ligação é encerrada." : "Escreva o recado — sem ele, a ligação só é desligada."}
              />
            )}
            <div className={classes.help}>
              {(cfg.voicemail || "hangup") === "off"
                ? "A IA conversa com quem atender, inclusive gravações."
                : "Vale para ligações feitas pelo agente (não para as recebidas). Aparece como \"caixa postal\" em Conversas e no desfecho do fluxo."}
            </div>
          </div>
        </div>

        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Gravação</div>
            <div className={classes.secDesc}>Grava o áudio das ligações para ouvir depois na aba Conversas. A transcrição é guardada sempre.</div>
          </div>
          <div className={classes.secRight}>
            <FormControlLabel
              control={<Checkbox color="primary" checked={cfg.recordCalls === true} onChange={e => set("recordCalls", e.target.checked)} />}
              label="Gravar ligações"
            />
            {cfg.recordCalls === true && (
              <div className={classes.help}>
                Pela LGPD, avise o cliente de que a ligação é gravada — por exemplo, na saudação inicial.
              </div>
            )}
          </div>
        </div>

        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Transferências permitidas</div>
            <div className={classes.secDesc}>Quando e para quem passar a ligação para uma pessoa.</div>
          </div>
          <div className={classes.secRight}>
            <AgentTransferEditor value={cfg.transferDestinations} onChange={v => set("transferDestinations", v)} />
          </div>
        </div>

        </>
        )}

        {tab === "canais" && agent && canal === "whatsapp" && (
        <>
          <AgentCanais only="whatsapp" agentId={agentId} agentName={agent.name} projectId={agent.projectId} numbers={linkedNumbers} onChange={loadLinked} historyValue={cfg.inboundHistory} onHistoryChange={v => set("inboundHistory", v)} />
        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Modelo</div>
            <div className={classes.secDesc}>A IA que responde nas conversas por escrito (WhatsApp e teste por texto).</div>
          </div>
          <div className={classes.secRight}>
            <TextField select fullWidth variant="outlined" size="small" value={cfg.chatModel || "gpt-4o"} onChange={e => set("chatModel", e.target.value)}
              helperText="Claude segue melhor a persona (não inventar dado, focar no objetivo). Sonnet 5 é o equilíbrio; Opus 5 para bots difíceis.">
              <MenuItem value="gpt-4o">GPT-4o (OpenAI) — padrão</MenuItem>
              <MenuItem value="claude-sonnet-5">Claude Sonnet 5 — recomendado p/ navegação</MenuItem>
              <MenuItem value="claude-opus-5">Claude Opus 5 — mais forte</MenuItem>
              <MenuItem value="claude-haiku-4-5-20251001">Claude Haiku 4.5 — rápido/barato</MenuItem>
            </TextField>
          </div>
        </div>

        <div className={classes.section}>
          <div>
            <div className={classes.secLabel}>Humanização</div>
            <div className={classes.secDesc}>Faz a IA responder como gente no WhatsApp: marca lida, mostra "digitando" e quebra a resposta em mensagens curtas, com ritmo.</div>
          </div>
          <div className={classes.secRight}>
            <FormControlLabel
              control={<Checkbox color="primary" checked={h.enabled !== false} onChange={e => setH("enabled", e.target.checked)} />}
              label="Humanizar respostas"
            />
            <div style={{ opacity: h.enabled === false ? 0.5 : 1, pointerEvents: h.enabled === false ? "none" : "auto" }}>
              <FormControlLabel
                control={<Checkbox color="primary" checked={h.markAsRead !== false} onChange={e => setH("markAsRead", e.target.checked)} />}
                label="Marcar como lida (✓✓)"
              />
              <FormControlLabel
                control={<Checkbox color="primary" checked={h.typingIndicator !== false} onChange={e => setH("typingIndicator", e.target.checked)} />}
                label={'Mostrar "digitando"'}
              />
              <FormControlLabel
                control={<Checkbox color="primary" checked={h.reactionsEnabled !== false} onChange={e => setH("reactionsEnabled", e.target.checked)} />}
                label="Reagir com emoji (👍/❤️) a elogios"
              />
              <FormControlLabel
                control={<Checkbox color="primary" checked={h.quoteReplies === true} onChange={e => setH("quoteReplies", e.target.checked)} />}
                label="Responder citando a mensagem do cliente"
              />
              <div className={classes.row} style={{ marginTop: 10 }}>
                <TextField type="number" variant="outlined" size="small" label="Tamanho da mensagem" value={h.chunkSize ?? 200}
                  onChange={e => setH("chunkSize", Number(e.target.value) || 200)} inputProps={{ min: 60, max: 1000 }} style={{ width: 180 }}
                  helperText="caracteres por mensagem" />
                <TextField type="number" variant="outlined" size="small" label="Máx. de mensagens" value={h.maxChunks ?? 4}
                  onChange={e => setH("maxChunks", Number(e.target.value) || 4)} inputProps={{ min: 1, max: 8 }} style={{ width: 160 }}
                  helperText="por resposta" />
              </div>
            </div>
          </div>
        </div>

        </>
        )}

        {tab === "eventos" && agent && (
          <AgentEventos agentId={agentId} connectors={connectors} projectId={agent.projectId} />
        )}

        {tab === "correcoes" && agent && (
          <AgentCorrecoes agentId={agentId} />
        )}

        {tab === "versoes" && agent && (
          <AgentVersoes agentId={agentId} />
        )}


        {tab === "conversas" && agent && (
          <AgentConversations agentId={agentId} initialConvKey={deepLinkConv} />
        )}
      </div>

      <Menu
        anchorEl={testMenu}
        open={!!testMenu}
        onClose={() => setTestMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: estilos.paper }}
        MenuListProps={{ disablePadding: true }}
      >
        <MenuItem className={estilos.item} onClick={() => { setTestMenu(null); setVoiceOpen(true); }}>
          <MicIcon fontSize="small" /> Conversar por voz
        </MenuItem>
        {/* todos os itens com ícone: um item sem ícone quebra o alinhamento da coluna */}
        <MenuItem className={estilos.item} onClick={() => { setTestMenu(null); setChatOpen(true); }}>
          <ChatIcon fontSize="small" /> Conversar por texto
        </MenuItem>
        <MenuItem className={estilos.item} onClick={() => { setTestMenu(null); setCallOpen(true); }}>
          <CallIcon fontSize="small" /> Ligar para testar
        </MenuItem>
      </Menu>

      <AgentVoiceTester
        open={voiceOpen}
        onClose={() => setVoiceOpen(false)}
        agentId={agentId}
        beforeStart={async () => {
          if (agent) await api.put(`/applications/${agentId}`, { name: name.trim() || agent.name, triggerConfig: cfg });
        }}
      />

      <AgentChatTester
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        agentId={agentId}
        beforeSend={async () => {
          if (agent) await api.put(`/applications/${agentId}`, { name: name.trim() || agent.name, triggerConfig: cfg });
        }}
      />

      <Dialog open={callOpen} onClose={() => setCallOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Ligar para testar</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 12.5, color: "#777", marginBottom: 14 }}>
            O agente liga para o destino e conversa usando a persona, voz e ferramentas configuradas.
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Ligar para (destino)"
            placeholder="+5511999998888"
            value={callTo}
            onChange={e => setCallTo(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <NumberSelect value={callFrom} onChange={setCallFrom} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={startCall} disabled={calling}>
            {calling ? "Iniciando…" : "Ligar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={saved}
        autoHideDuration={2500}
        onClose={() => setSaved(false)}
        message="Agente salvo."
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </div>
  );
};

export default AgentEditor;
